import { useEffect } from 'react'
import { Link, matchPath, useLocation } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { Menu, Avatar } from './ui.jsx'
import { usePanelAyuda } from '../lib/ayuda.jsx'

const Check = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ marginLeft: 'auto' }}>
    <path d="M13 4.5L6.5 11L3 7.5" stroke="currentColor" strokeWidth="1.8"
          strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)
const Caret = () => (
  <svg width="11" height="11" viewBox="0 0 16 16" fill="none" style={{ color: 'var(--fg-3)' }}>
    <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.6"
          strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

/** Barra superior: marca, selector de proyecto y menú de usuario con el avatar de GitLab. */
export function Shell({ me, onLogout, children }) {
  // useParams() aquí devolvía siempre vacío: el Shell envuelve al <Routes>, y fuera de una ruta
  // casada no hay parámetros. El selector de proyecto llevaba invisible desde el principio por
  // esto. matchPath lee directamente de la URL y no depende del contexto de ruta.
  const { pathname } = useLocation()
  const slug = (matchPath('/p/:slug/*', pathname) ?? matchPath('/p/:slug', pathname))?.params.slug
  const projects = useQuery({ queryKey: ['projects'], queryFn: api.projects, staleTime: 30_000 })
  const ayuda = usePanelAyuda()
  const list = projects.data?.projects ?? []

  // De paso, la pestaña del navegador también dice dónde estás.
  useEffect(() => {
    document.title = slug ? `${slug} · Consola` : 'Consola'
  }, [slug])

  return (
    <>
      <header className="topbar">
        <Link to="/" className="brand">
          <img className="brand-mark" src="/marca.svg" alt="" width="22" height="22" />
          <span>Consola</span>
        </Link>

        {slug && (
          <>
            <span className="slash">/</span>
            <Menu trigger={<><span style={{ fontWeight: 500 }}>{slug}</span><Caret /></>}>
              <div className="menu-label">Proyectos</div>
              {list.map((p) => (
                <Link key={p.slug} className="menu-item" to={`/p/${p.slug}`} role="menuitem">
                  {p.slug}
                  {p.slug === slug && <Check />}
                </Link>
              ))}
              <div className="menu-sep" />
              <Link className="menu-item" to="/" role="menuitem">Ver todos</Link>
            </Menu>
          </>
        )}

        <span className="topbar-spacer" />

        <button className="atajo-k" title="Paleta de comandos"
                onClick={() => window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }))}>
          ⌘K
        </button>

        {ayuda && (
          <button className="btn-ayuda" title="Ayuda" aria-label="Ayuda"
                  aria-pressed={ayuda.abierta} onClick={ayuda.alternar}>?</button>
        )}

        <Menu align="right" trigger={<Avatar src={me.avatar} name={me.name} email={me.email} />}>
          <div style={{ padding: '9px 10px', display: 'flex', gap: 10, alignItems: 'center' }}>
            <Avatar src={me.avatar} name={me.name} email={me.email} lg />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 500 }}>{me.name ?? me.email}</div>
              <div style={{ color: 'var(--fg-3)', fontSize: 12 }} className="ellipsis">{me.email}</div>
            </div>
          </div>
          {me.platformAdmin && (
            <>
              <div className="menu-sep" />
              <div className="menu-label">Plataforma</div>
              <Link className="menu-item" to="/admin/health" role="menuitem">Diagnóstico</Link>
              <Link className="menu-item" to="/admin/sessions" role="menuitem">Sesiones activas</Link>
              <Link className="menu-item" to="/admin/audit" role="menuitem">Registro de auditoría</Link>
            </>
          )}
          <div className="menu-sep" />
          <button className="menu-item danger" onClick={onLogout} role="menuitem">Cerrar sesión</button>
        </Menu>
      </header>
      {children}
    </>
  )
}
